import { supabase } from '../lib/supabase';
import type { GeneratedAsset } from '../lib/db/types';
import { isLocalPublicAssetPath, selectViewerGlb } from './selectViewerGlb';

const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;
const NEGATIVE_CACHE_TTL_MS = 60 * 1000;

type CacheEntry = {
  expiresAt: number;
  resolvedUrl?: string;
};

const previewResolveCache = new Map<string, CacheEntry>();
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

export interface ValidatedFrontPageAsset<T extends GeneratedAsset = GeneratedAsset> {
  asset: T;
  glbUrl: string;
  resolvedGlbUrl: string;
}

const normalizeLocalPath = (value: string) => (value.startsWith('/') ? value : `/${value}`);

export async function resolveValidPublicPreviewUrl(asset: GeneratedAsset): Promise<{
  glbUrl: string;
  resolvedGlbUrl: string;
} | null> {
  const glbUrl = selectViewerGlb(asset, false)?.trim();
  if (!glbUrl) return null;

  if (isLocalPublicAssetPath(glbUrl)) {
    return {
      glbUrl,
      resolvedGlbUrl: normalizeLocalPath(glbUrl),
    };
  }

  const cacheKey = `${asset.id}|${glbUrl}`;
  const cached = previewResolveCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    if (!cached.resolvedUrl) return null;
    return { glbUrl, resolvedGlbUrl: cached.resolvedUrl };
  }

  try {
    const { data, error } = await supabase.functions.invoke('assets-preview-resolve', {
      headers: supabaseAnonKey
        ? {
            apikey: supabaseAnonKey,
            Authorization: `Bearer ${supabaseAnonKey}`,
          }
        : undefined,
      body: {
        assetId: asset.id,
        hintPath: glbUrl,
      },
    });

    const resolvedUrl = typeof (data as { url?: unknown } | null)?.url === 'string'
      ? (data as { url: string }).url
      : undefined;

    if (error || !resolvedUrl) {
      previewResolveCache.set(cacheKey, {
        resolvedUrl: undefined,
        expiresAt: Date.now() + NEGATIVE_CACHE_TTL_MS,
      });
      return null;
    }

    const ttlSeconds = Number((data as { cacheTtlSeconds?: unknown } | null)?.cacheTtlSeconds);
    previewResolveCache.set(cacheKey, {
      resolvedUrl,
      expiresAt: Date.now() + (Number.isFinite(ttlSeconds) && ttlSeconds > 0 ? ttlSeconds * 1000 : DEFAULT_CACHE_TTL_MS),
    });

    return { glbUrl, resolvedGlbUrl: resolvedUrl };
  } catch {
    previewResolveCache.set(cacheKey, {
      resolvedUrl: undefined,
      expiresAt: Date.now() + NEGATIVE_CACHE_TTL_MS,
    });
    return null;
  }
}

export async function filterAssetsWithValidPublicPreviews<T extends GeneratedAsset>(
  assets: T[],
  limit: number,
  chunkSize = 3
): Promise<Array<ValidatedFrontPageAsset<T>>> {
  if (limit <= 0 || assets.length === 0) return [];

  const valid: Array<ValidatedFrontPageAsset<T>> = [];
  for (let i = 0; i < assets.length && valid.length < limit; i += chunkSize) {
    const chunk = assets.slice(i, i + chunkSize);
    const resolvedChunk = await Promise.all(
      chunk.map(async (asset) => {
        const resolved = await resolveValidPublicPreviewUrl(asset);
        if (!resolved) return null;
        return {
          asset,
          glbUrl: resolved.glbUrl,
          resolvedGlbUrl: resolved.resolvedGlbUrl,
        } satisfies ValidatedFrontPageAsset<T>;
      })
    );

    for (const entry of resolvedChunk) {
      if (!entry) continue;
      valid.push(entry);
      if (valid.length >= limit) break;
    }
  }

  return valid;
}
